import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import { api } from "../services/api";
import { createEnrichmentTask } from "wasp/client/operations";
import { EnrichmentConfig, Filters } from "../types/api";
import { updateCreditBalance, getUserSubscriptions } from "wasp/client/operations";
import { setJobFilter } from "../store/filtersSlice";
import { analytics } from "../analytics";

type UseStartEnrichmentProps = {
  onJobStarted: () => void;
};

type StartEnrichmentProps = {
  config: EnrichmentConfig;
  usage: number;
  productIds?: string[];
  filters?: Filters;
  productCount?: number;
}

type Status = "idle" | "in_progress" | "completed";

type UseStartEnrichmentHook = {
  startEnrichment: (props: StartEnrichmentProps) => void;
  error: string | null;
  status: string;
};

export const useStartEnrichment = ({
  onJobStarted,
}: UseStartEnrichmentProps): UseStartEnrichmentHook => {
  const { currentUser, currentCollection } = useSelector(
    (state: RootState) => state.user
  );
  const dispatch = useDispatch();
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("idle");

  const startEnrichment = async ({ 
    config, 
    usage, 
    productIds, 
    filters,
    productCount
  }: StartEnrichmentProps) => {
    setError(null);
    setStatus("in_progress");
    try {
      if (!currentUser) {
        throw new Error("User not logged in");
      }
      if (!currentCollection?.collection?.tableId) {
        throw new Error(
          "Collection has not imported any products. Table Id not found."
        );
      }

      const res = await api.postEnrich({
        tableId: currentCollection?.collection?.tableId,
        config: config,
        ...(productIds && { product_ids: productIds }),
        ...(filters && { filters }),
      });

      if (res.error) {
        throw new Error(res.error.message);
      }
      if (!res.data) {
        throw new Error("No data found in response");
      }

      const { job_id } = res.data;

      if (!job_id) {
        throw new Error("Job ID not found in response");
      }

      const featuresList : string[] = []

      if(config.alt_text.generate){
        featuresList.push("alt_text")
      }
      if(config.description.generate){
        featuresList.push("description")
      }
      if(config.image_finder.generate){
        featuresList.push("image_finder")
      }
      if(config.market_radar.generate){
        featuresList.push("market_radar")
      }
      if(config.product_tags.generate){
        featuresList.push("product_tags")
      }
      if(config.seo.generate){
        featuresList.push("seo")
      }
      if(config.title.generate){
        featuresList.push("title")
      }


      analytics.sendEnhancementJobStartedEvent({
        email: currentUser.email ?? "unknown",
        jobId: job_id,
        features: featuresList
      })

      await new Promise((resolve) => setTimeout(resolve, 1000));

      await createEnrichmentTask({
        enrichmentJobId: job_id,
        collectionId: currentCollection?.collection.id,
        productCount: productCount
      });

      const subs = await getUserSubscriptions({ userId: currentUser.id });
      if (subs.length === 0) {
        throw new Error("User has no subscription");
      }
      await updateCreditBalance({
        userId: currentUser.id,
        creditsChange: -usage,
        subscriptionId: subs[0].id
      })
      onJobStarted && onJobStarted();
      dispatch(setJobFilter("running"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
      setStatus("idle");
    }
  };

  return {
    startEnrichment,
    error,
    status,
  };
};
